import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import API from '../../services/api';

// Shell-wide state for the Admin/Super-Admin shell: the nav sheet (mobile drawer) open state, the
// profile sheet open state, and — super admin only — a real "new" feedback count for the header badge.
// Nothing here is fabricated: a college admin has no cross-tenant count to show (they cannot see other
// colleges' or their own submitted-feedback triage state as a meaningful "unread" signal), so newCount
// stays null for them and the header simply shows no badge, instead of inventing one.
const AdminContext = createContext(null);

export function AdminProvider({ role, children }) {
  // `role` is the same value App.js already computed per-route for the old <Layout role="...">
  // (some routes key off realRole to stay "super_admin" while browsing a specific college; others
  // off role) — honoring it here keeps that per-route decision instead of re-deriving it differently.
  const isSuper = role === 'super_admin';
  const [navOpen, setNavOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [newFeedbackCount, setNewFeedbackCount] = useState(null);

  const refreshFeedbackCount = useCallback(async () => {
    if (!isSuper) return;
    try {
      const res = await API.get('/feedback', { params: { limit: 1 } });
      setNewFeedbackCount(res.data?.counts?.new ?? 0);
    } catch { /* the header simply shows no badge */ }
  }, [isSuper]);

  useEffect(() => { refreshFeedbackCount(); }, [refreshFeedbackCount]);

  const value = useMemo(() => ({
    isSuper, navOpen, openNav: () => setNavOpen(true), closeNav: () => setNavOpen(false),
    profileOpen, openProfile: () => setProfileOpen(true), closeProfile: () => setProfileOpen(false),
    newFeedbackCount, refreshFeedbackCount,
  }), [isSuper, navOpen, profileOpen, newFeedbackCount, refreshFeedbackCount]);

  return <AdminContext.Provider value={value}>{children}</AdminContext.Provider>;
}

export function useAdminShell() {
  const ctx = useContext(AdminContext);
  if (ctx) return ctx;
  return { isSuper: false, navOpen: false, openNav: () => {}, closeNav: () => {}, profileOpen: false, openProfile: () => {}, closeProfile: () => {}, newFeedbackCount: null, refreshFeedbackCount: async () => {} };
}
